import React, { useState, useEffect } from 'react';
import { useExclusiveAudio } from '../utils/useExclusiveAudio';
import { audioCoordinator } from '../utils/audioCoordinator';
import { FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

function initialVideos() {
  // Reuse the captured playlist on hydration; discovery can refresh it afterward.
  const playlist = document.getElementById('testimonials')?.dataset.videoUrls;
  if (!playlist) return [];
  try {
    const urls = JSON.parse(playlist);
    return Array.isArray(urls) && urls.every(url => typeof url === 'string') ? urls : [];
  } catch {
    return [];
  }
}

function VideoTestimonials() {
  const { videoRef, setVideoRef, isMuted, blocked, toggleMute } = useExclusiveAudio();
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoUrls, setVideoUrls] = useState(initialVideos);
  const [isLoading, setIsLoading] = useState(() => videoUrls.length === 0);

  // Dynamically discover and shuffle all ref videos
  useEffect(() => {
    const discoverVideos = async () => {
      const baseUrl = 'https://storage.googleapis.com/lupatest/ref/ref';
      const discoveredVideos = [];

      // Fallback to known videos in case discovery fails
      const fallbackVideos = [
        'https://storage.googleapis.com/lupatest/ref/ref1.mp4',
        'https://storage.googleapis.com/lupatest/ref/ref2.mp4',
        'https://storage.googleapis.com/lupatest/ref/ref3.mp4',
        'https://storage.googleapis.com/lupatest/ref/ref4.mp4',
        'https://storage.googleapis.com/lupatest/ref/ref5.mp4',
      ];

      // Try to load each video directly
      for (let index = 1; index <= 10; index++) {
        const videoUrl = `${baseUrl}${index}.mp4`;

        try {
          // Try to create a video element to test if it loads
          const testVideo = document.createElement('video');
          testVideo.src = videoUrl;

          await new Promise((resolve, reject) => {
            testVideo.onloadedmetadata = () => {
              discoveredVideos.push(videoUrl);
              resolve();
            };
            testVideo.onerror = reject;
            // Timeout after 2 seconds
            setTimeout(reject, 2000);
          });
        } catch (error) {
          // Video doesn't exist or failed to load, continue
        }
      }

      // If no videos were discovered, use fallback
      const videosToUse = discoveredVideos.length > 0 ? discoveredVideos : fallbackVideos;

      // Shuffle the videos using Fisher-Yates algorithm
      const shuffled = [...videosToUse];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      setVideoUrls(shuffled);
      setIsLoading(false);
    };

    discoverVideos();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let timer;
    const handleVideoEnd = () => {
      timer = setTimeout(() => {
        if (audioCoordinator.getVoice() === null) setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoUrls.length);
      }, 1000);
    };

    video.addEventListener('ended', handleVideoEnd);
    return () => { clearTimeout(timer); video.removeEventListener('ended', handleVideoEnd); };
  }, [currentVideoIndex, videoUrls.length, videoRef]);

  if (isLoading) {
    return (
      <section className="video-testimonials-section" id="testimonials">
        <div className="container">
          <h2 className="video-testimonials-title">What Our Clients Say</h2>
          <p className="video-testimonials-subtitle">Loading testimonials...</p>
        </div>
      </section>
    );
  }

  if (videoUrls.length === 0) {
    return null; // Don't render if no videos found
  }

  return (
    <section className="video-testimonials-section" id="testimonials" data-video-urls={JSON.stringify(videoUrls)}>
      <div className="container">
        <h2 className="video-testimonials-title">What Our Clients Say</h2>
        <p className="video-testimonials-subtitle">
          Hear directly from businesses that transformed their customer service with Ubotika
        </p>

        <div className="video-testimonials-content">
          <div className="video-testimonials-player">
            <video
              ref={setVideoRef}
              src={videoUrls[currentVideoIndex]}
              className="testimonial-video"
              autoPlay={!blocked}
              muted={isMuted}
              playsInline
              key={currentVideoIndex}
            />
            <button
              className="video-mute-button"
              onClick={toggleMute}
              disabled={blocked}
              title={blocked ? "Close the voice demo to listen to testimonials" : undefined}
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>

            <div className="video-indicators">
              {videoUrls.map((_, index) => (
                <button
                  key={index}
                  className={`video-indicator ${index === currentVideoIndex ? 'active' : ''}`}
                  disabled={blocked}
                  onClick={() => { if (audioCoordinator.getVoice() === null) setCurrentVideoIndex(index); }}
                  aria-label={`Play testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default VideoTestimonials;
